// Textarea — the multi-line text-entry primitive, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// Same near-monochrome, borderless register as the other controls: a tinted
// resting surface with NO edge painted at rest; an edge is REVEALED by
// interaction (hover 1px, focus 2px) — the border→elevation discipline (§2).
//
// Auto-grows WITHOUT any JS measurement. The wrapper mirrors the current
// value into a hidden ::after that shares grid cell 1/1 with the field, so
// the row height tracks the wrapped text and the field stretches to fill it
// (see Textarea.css). Because the mirror reads `data-value`, auto-grow needs
// the CONTROLLED `value` prop — which is the pure path anyway: the text lives
// in the caller's state, and every keystroke is reported up via the caller's
// own onChange. Nothing is measured, stored, or mutated in here.
//
// `invalid` is a genuine STATE (not a meaning-swapping mode flag): it drives
// `aria-invalid` for assistive tech AND a persistent danger ring that is
// present at rest — an edge where valid fields show none — so the cue is not
// colour-alone (SC 1.4.1). `size` picks the density; every other textarea
// attribute (onChange, placeholder, disabled, aria-*, rows…) passes straight
// through. No intrinsic visible text lives here, so no Lingui macros are
// needed — the caller supplies the label/placeholder strings.

import type { Ref, TextareaHTMLAttributes } from "react";
import "./Textarea.css";

type TextareaSize = "sm" | "md" | "lg";

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
    readonly size?: TextareaSize;
    readonly invalid?: boolean;
    // Opt-in handle on the underlying <textarea>, so an impure host edge can
    // focus it (autofocus-on-arrival) without the pure tree reaching for the
    // DOM. Optional — existing callers pass nothing and are unaffected.
    readonly inputRef?: Ref<HTMLTextAreaElement>;
};

export const Textarea = ({
    size = "md",
    invalid = false,
    value,
    rows = 3,
    className,
    inputRef,
    ...rest
}: TextareaProps) => (
    // The wrapper carries the styled surface + the auto-grow mirror; the
    // caller's className lands here so layout (width, margins) targets the
    // control as a whole.
    <div
        className={`bilda-textarea bilda-textarea--${size}${
            invalid ? " bilda-textarea--invalid" : ""
        }${className ? ` ${className}` : ""}`}
        data-value={value}
    >
        <textarea
            ref={inputRef}
            className="bilda-textarea__field"
            rows={rows}
            {...rest}
            value={value}
            aria-invalid={invalid || undefined}
        />
    </div>
);
